import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  NgZone,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AutoFocusModule } from 'primeng/autofocus';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { Select, SelectChangeEvent, SelectModule } from 'primeng/select';
import { debounceTime, Subject } from 'rxjs';
import { CesIconComponent } from '../ces-icon/ces-icon.component';

@Component({
  selector: 'app-search-dropdown',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    SelectModule,
    AutoFocusModule,
    CesIconComponent,
  ],
  templateUrl: './search-dropdown.component.html',
  styleUrls: ['./search-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchDropdownComponent implements OnInit, AfterViewInit {
  @ViewChild('selectComponent') selectComponent!: Select;

  @Input() options: unknown[] = [];
  @Input() placeholder: string = 'Rechercher...';
  @Input() iconClass: string = 'pi pi-search';
  @Input() loading: boolean = false;
  @Input() group: boolean = false;
  @Input() optionLabel: string = 'label';
  @Input() optionValue: string = 'value';
  @Input() optionGroupLabel: string = 'label';
  @Input() optionGroupChildren: string = 'items';
  @Input() debounceTimeMs: number = 100;
  @ContentChild('groupTemplate') groupTemplate: TemplateRef<unknown> | null = null;
  @ContentChild('itemTemplate') itemTemplate: TemplateRef<unknown> | null = null;
  @Input() openOnFocus: boolean = true;

  @Output() search = new EventEmitter<string>();
  @Output() optionSelected = new EventEmitter<SelectChangeEvent>();
  @Output() cleared = new EventEmitter<void>();

  isSelectVisible: boolean = false;
  searchQuery: string = '';
  selectedOption: unknown = null;
  dropdownActive: boolean = false;
  hasSelectFocus: boolean = false;
  private pendingDropdownOpen: boolean = false;
  private justCleared: boolean = false;
  private checkSelectVisibilityTimeout: ReturnType<typeof setTimeout> | null = null;

  private readonly searchTerm$ = new Subject<string>();

  constructor(
    private readonly cd: ChangeDetectorRef,
    private readonly zone: NgZone,
  ) {}

  ngOnInit(): void {
    this.searchTerm$.pipe(debounceTime(this.debounceTimeMs)).subscribe(term => {
      this.search.emit(term);
    });
  }

  ngAfterViewInit(): void {
    if (this.pendingDropdownOpen && this.selectComponent) {
      this.openDropdownDelayed();
    }
  }

  public reset(): void {
    this.searchQuery = '';
    this.selectedOption = null;
    this.isSelectVisible = false;
    this.cleared.emit();
  }

  private shouldHideSelect(): boolean {
    return (
      !this.selectedOption &&
      !this.dropdownActive &&
      (!this.searchQuery || this.searchQuery.trim().length === 0) &&
      !this.hasSelectFocus
    );
  }

  private checkSelectVisibility(): void {
    if (this.checkSelectVisibilityTimeout) {
      clearTimeout(this.checkSelectVisibilityTimeout);
    }

    this.checkSelectVisibilityTimeout = setTimeout(() => {
      if (this.shouldHideSelect()) {
        this.isSelectVisible = false;
        this.cd.detectChanges();
      }
      this.checkSelectVisibilityTimeout = null;
    }, 150);
  }

  protected showSelect(): void {
    this.isSelectVisible = true;
    this.pendingDropdownOpen = true;

    this.cd.detectChanges();

    if (this.selectComponent && this.openOnFocus) {
      this.openDropdownDelayed();
    }
  }

  protected onSelectFocus(): void {
    this.hasSelectFocus = true;

    if (this.openOnFocus && !this.dropdownActive) {
      this.openDropdownDelayed();
    }
  }

  private openDropdownDelayed(): void {
    this.zone.runOutsideAngular(() => {
      setTimeout(() => {
        this.zone.run(() => {
          if (this.selectComponent && !this.dropdownActive) {
            this.selectComponent.show(true);
            this.pendingDropdownOpen = false;
          }
        });
      }, 0);
    });
  }

  protected onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchQuery = input.value;

    if (this.searchQuery && this.searchQuery.trim().length > 0) {
      this.showSelect();
      this.searchTerm$.next(this.searchQuery);
    }
  }

  protected onSelectShow(): void {
    this.isSelectVisible = true;
    this.dropdownActive = true;
  }

  protected onSelectHide(): void {
    this.dropdownActive = false;

    if (this.justCleared) {
      this.justCleared = false;

      setTimeout(() => {
        this.openDropdownDelayed();
      }, 50);
      return;
    }

    this.checkSelectVisibility();
  }

  protected onSelectBlur(): void {
    this.hasSelectFocus = false;

    if (this.dropdownActive) {
      return;
    }

    this.checkSelectVisibility();
  }

  protected onOptionChange(event: SelectChangeEvent): void {
    if (event.value && typeof event.value !== 'string') {
      this.selectedOption = event.value;
      this.optionSelected.emit(event);
    } else if (typeof event.value === 'string') {
      this.searchQuery = event.value;
      this.searchTerm$.next(event.value);
    }
  }

  protected onSelectClear(): void {
    this.searchQuery = '';
    this.selectedOption = null;

    this.justCleared = true;

    this.cleared.emit();

    setTimeout(() => {
      const focusEl = this.selectComponent.focusInputViewChild?.nativeElement as HTMLInputElement | undefined;
      const editableEl = this.selectComponent.editableInputViewChild?.nativeElement as HTMLInputElement | undefined;
      const inputEl = focusEl ?? editableEl;
      if (inputEl) {
        inputEl.focus();
        this.hasSelectFocus = true;
      }

      this.openDropdownDelayed();
    }, 50);
  }
}
